import { State } from 'country-state-city';
import { FC, ReactElement, useState, useRef, useEffect, useCallback } from 'react';

import { CLICK_OUTSIDE_EVENT } from '../constants/event.constants';
import { 
    PLACEHOLDER_PROVINCE, 
    PLACEHOLDER_STATE, 
    NO_OPTIONS_FOUND,
    KEY_BACKSPACE,
    KEY_ESCAPE,
    EVENT_KEYDOWN,
    COUNTRY_CODE_CA 
} from '../constants/stateSelect.constants';

export interface StateData {
    name: string;
    isoCode: string;
    countryCode: string;
}

interface StateSelectProps {
    countryCode: string; // e.g. 'US' or 'CA'
    value: string; // The selected state's name or isoCode
    onChange: (stateName: string) => void;
    label: string;
    error?: string;
    onInteraction?: () => void;
    labelWidthClass?: string;
}

const StateSelect: FC<StateSelectProps> = ({
    countryCode,
    value,
    onChange,
    label,
    error,
    onInteraction,
    labelWidthClass
}): ReactElement => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [states, setStates] = useState<StateData[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filteredStates, setFilteredStates] = useState<StateData[]>([]);
    const [activeFilter, setActiveFilter] = useState<string>('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const countryStates = State.getStatesOfCountry(countryCode) as StateData[];
        setStates(countryStates);
        setFilteredStates(countryStates);
    }, [countryCode]);

    // Update filtered states when activeFilter or states change
    useEffect(() => {
        if (activeFilter) {
            const filtered = states.filter(state =>
                state.name.toLowerCase().startsWith(activeFilter.toLowerCase())
            );
            setFilteredStates(filtered.length > 0 ? filtered : states);
        } else {
            setFilteredStates(states);
        }
    }, [activeFilter, states]);

    // Handle keyboard input for type-ahead search
    const handleKeyDown = useCallback((e: globalThis.KeyboardEvent) => {
        if (!isOpen) return;

        if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
            e.preventDefault();

            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }

            const newSearchTerm = searchTerm + e.key;
            setSearchTerm(newSearchTerm);
            setActiveFilter(newSearchTerm);

            resetTimeoutRef.current = setTimeout(() => {
                setSearchTerm('');
            }, 1500);
        }

        if (e.key === KEY_BACKSPACE) {
            e.preventDefault();
            setSearchTerm('');
            setActiveFilter('');
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }
        }

        if (e.key === KEY_ESCAPE) {
            setIsOpen(false);
            setSearchTerm('');
            setActiveFilter('');
        }
    }, [isOpen, searchTerm]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener(EVENT_KEYDOWN, handleKeyDown);
        }
        return () => {
            document.removeEventListener(EVENT_KEYDOWN, handleKeyDown);
        };
    }, [isOpen, handleKeyDown]);

    useEffect(() => {
        return () => {
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener(CLICK_OUTSIDE_EVENT, handleClickOutside);
        return () => document.removeEventListener(CLICK_OUTSIDE_EVENT, handleClickOutside);
    }, []);

    const handleSelect = (stateData: StateData) => {
        onChange(stateData.name);
        if (onInteraction) onInteraction();
        setIsOpen(false);
        setSearchTerm('');
        setActiveFilter('');
    };

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (isOpen) {
            setSearchTerm('');
            setActiveFilter('');
        }
        if (onInteraction && !isOpen) onInteraction();
    };

    const placeholderText = countryCode === COUNTRY_CODE_CA ? PLACEHOLDER_PROVINCE : PLACEHOLDER_STATE;
    const selectedStateName = value || placeholderText;

    return (
        <div className="flex flex-col bg-white w-full">
            <div className="flex items-center justify-between mb-[6px]">
                <label className="flex items-center font-noto font-medium text-[14px] text-[#344054] leading-[24px]">
                    <span className={`inline-block h-[24px] leading-[24px] ${labelWidthClass || 'w-[124px]'}`}>{label}</span>
                </label>
            </div>
            <div className="relative w-full" ref={dropdownRef}>
                <button
                    type="button"
                    onClick={handleToggle}
                    className={`flex items-center justify-between w-full h-[40px] py-[10px] px-[12px] border ${error ? 'border-red-500' : 'border-[#667085]'} rounded-[6px] text-[14px] font-noto font-medium ${value ? 'text-gray-900' : 'text-[#667085]'} bg-white focus:outline-none focus:border-[#030407] transition-colors`}
                >
                    <span className="truncate font-normal inline-block w-[228.5px] h-[20px] leading-[20px] text-left">{selectedStateName}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className={`w-[20px] h-[20px] flex-shrink-0 text-[#98A2B3] transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                </button>

                {isOpen && (
                    <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-20 max-h-60 overflow-y-auto">
                        {filteredStates.length > 0 ? (
                            filteredStates.map((state) => (
                                <button
                                    key={state.isoCode}
                                    type="button"
                                    className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors text-gray-700 font-medium"
                                    onClick={() => handleSelect(state)}
                                >
                                    {state.name}
                                </button>
                            ))
                        ) : (
                            <div className="px-4 py-2 text-sm text-gray-500">{NO_OPTIONS_FOUND}</div>
                        )}
                    </div>
                )}
            </div>
            {error && (
                <p className="mt-1 font-noto font-normal text-[12px] leading-[18px] text-[#D92D20]">
                    {error}
                </p>
            )}
        </div>
    );
};

export default StateSelect;
